import { useState } from "react";
import { motion } from "framer-motion";
import { HandHeart, Loader2, CheckCircle, ShieldCheck } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { useLanguage } from "../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COPY = {
  en: {
    overline: "Volunteer",
    title: "Give Your Time to Something That Matters.",
    subtitle: "The Pillar Project welcomes structured, trauma-informed volunteers. Volunteers help extend the reach of advocacy, events, workforce coaching, and community outreach — without directly managing survivor crises or providing unsupervised advocacy.",
    screeningTitle: "Screening & Training",
    screeningText: "Volunteers may be asked to complete an application, interview, background screening, confidentiality agreement, trauma-informed training, and role-specific orientation. This protects both survivors and volunteers.",
    formTitle: "Volunteer Interest Form",
    submittedTitle: "Application Received",
    submittedText: "Thank you for stepping forward. A team member will reach out with next steps.",
    interests: ["Community outreach","Event support","Administrative assistance","Resume and interview coaching","Professional clothing coordination","Donation organization","Training-event logistics","Fundraising","Communications","Data entry","Resource research","Professional services (legal / mental health / medical / financial)"],
    labels: {
      name: "Name", email: "Email", phone: "Phone", availability: "Availability", availabilityPlaceholder: "e.g. Weekday evenings, weekends",
      interests: "Areas of Interest (select all that apply)", experience: "Relevant Experience or Skills", comments: "Additional Comments",
      consent: "I understand that volunteer roles may require a background screening, confidentiality agreement, and role-specific training. I consent to those steps as needed for placement.",
      submit: "Submit Interest", submitting: "Submitting…",
    },
    toastConsent: "Please confirm consent to background screening to proceed.",
    toastSuccess: "Volunteer application submitted",
    toastError: "Submission failed",
  },
  es: {
    overline: "Voluntariado",
    title: "Dona Tu Tiempo a Algo Que Importa.",
    subtitle: "The Pillar Project acoge voluntarios estructurados e informados sobre el trauma. Los voluntarios ayudan a extender el alcance de defensa, eventos, coaching laboral y alcance comunitario — sin manejar directamente crisis de sobrevivientes ni proporcionar defensa sin supervisión.",
    screeningTitle: "Verificación y Capacitación",
    screeningText: "Se puede pedir a los voluntarios que completen una solicitud, entrevista, verificación de antecedentes, acuerdo de confidencialidad, capacitación informada sobre el trauma y orientación específica del rol. Esto protege tanto a los sobrevivientes como a los voluntarios.",
    formTitle: "Formulario de Interés de Voluntariado",
    submittedTitle: "Solicitud Recibida",
    submittedText: "Gracias por dar el paso. Un miembro del equipo se comunicará contigo con los próximos pasos.",
    interests: ["Alcance comunitario","Apoyo en eventos","Asistencia administrativa","Coaching de currículum y entrevistas","Coordinación de ropa profesional","Organización de donaciones","Logística de eventos de capacitación","Recaudación de fondos","Comunicaciones","Entrada de datos","Investigación de recursos","Servicios profesionales (legales / salud mental / médicos / financieros)"],
    labels: {
      name: "Nombre", email: "Correo", phone: "Teléfono", availability: "Disponibilidad", availabilityPlaceholder: "p.ej. Tardes de semana, fines de semana",
      interests: "Áreas de Interés (selecciona todas las que apliquen)", experience: "Experiencia o Habilidades Relevantes", comments: "Comentarios Adicionales",
      consent: "Entiendo que los roles de voluntariado pueden requerir verificación de antecedentes, acuerdo de confidencialidad y capacitación específica del rol. Consiento a esos pasos según sea necesario para la colocación.",
      submit: "Enviar Interés", submitting: "Enviando…",
    },
    toastConsent: "Por favor confirma el consentimiento a la verificación de antecedentes para continuar.",
    toastSuccess: "Solicitud de voluntariado enviada",
    toastError: "El envío falló",
  },
};

export default function VolunteerPage() {
  const { language } = useLanguage();
  const c = COPY[language] || COPY.en;

  const [form, setForm] = useState({
    name: "", email: "", phone: "", interest_areas: [],
    availability: "", experience: "", consent_background_check: false, additional_comments: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const toggleInterest = (area) => {
    setForm((f) => ({ ...f, interest_areas: f.interest_areas.includes(area) ? f.interest_areas.filter((a) => a !== area) : [...f.interest_areas, area] }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent_background_check) return toast.error(c.toastConsent);
    setSubmitting(true);
    try {
      await axios.post(`${API}/volunteer/apply`, { ...form, interest_areas: form.interest_areas.join(", ") });
      toast.success(c.toastSuccess);
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || c.toastError);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <section className="bg-warm-800 text-white py-16 md:py-24" data-testid="volunteer-hero">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-warm-300 mb-4 block">{c.overline}</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.1] mb-6" style={{ fontFamily: "Cormorant Garamond, Georgia, serif" }} data-testid="volunteer-title">{c.title}</h1>
            <p className="text-lg text-warm-200 leading-relaxed max-w-2xl">{c.subtitle}</p>
          </motion.div>
        </div>
      </section>

      <section className="py-8 bg-warm-50 border-b border-warm-100">
        <div className="max-w-3xl mx-auto px-6 flex gap-4">
          <ShieldCheck className="w-6 h-6 text-warm-700 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-gray-700">
            <p className="font-semibold text-gray-900 mb-1">{c.screeningTitle}</p>
            <p>{c.screeningText}</p>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-6">
          {submitted ? (
            <div className="bg-warm-50 border border-warm-100 rounded-xl p-8 text-center" data-testid="volunteer-submitted">
              <CheckCircle className="w-12 h-12 text-warm-700 mx-auto mb-3" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2" style={{ fontFamily: "Cormorant Garamond, Georgia, serif" }}>{c.submittedTitle}</h3>
              <p className="text-gray-600">{c.submittedText}</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="volunteer-form">
              <div className="flex items-center gap-3 mb-2">
                <HandHeart className="w-6 h-6 text-warm-700" />
                <h2 className="text-2xl font-semibold text-gray-900" style={{ fontFamily: "Cormorant Garamond, Georgia, serif" }}>{c.formTitle}</h2>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label={c.labels.name} required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="vol-name" />
                <Field label={c.labels.email} type="email" required value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="vol-email" />
                <Field label={c.labels.phone} value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testId="vol-phone" />
                <Field label={c.labels.availability} placeholder={c.labels.availabilityPlaceholder} value={form.availability} onChange={(v) => setForm({ ...form, availability: v })} testId="vol-availability" />
              </div>

              <div>
                <label className="text-xs font-medium text-gray-600 uppercase tracking-wider block mb-2">{c.labels.interests}</label>
                <div className="grid sm:grid-cols-2 gap-2">
                  {c.interests.map((a) => (
                    <label key={a} className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                      <input type="checkbox" checked={form.interest_areas.includes(a)} onChange={() => toggleInterest(a)} className="mt-1 accent-warm-700" data-testid={`vol-interest-${a.substring(0, 12).replace(/\s+/g, "-")}`} />
                      <span>{a}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs font-medium text-gray-600 uppercase tracking-wider block mb-1">{c.labels.experience}</label>
                <textarea className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm min-h-[90px]" value={form.experience} onChange={(e) => setForm({ ...form, experience: e.target.value })} data-testid="vol-experience" />
              </div>

              <div>
                <label className="text-xs font-medium text-gray-600 uppercase tracking-wider block mb-1">{c.labels.comments}</label>
                <textarea className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm min-h-[70px]" value={form.additional_comments} onChange={(e) => setForm({ ...form, additional_comments: e.target.value })} data-testid="vol-comments" />
              </div>

              <label className="flex items-start gap-3 bg-gray-50 border border-gray-100 rounded-lg p-4 text-sm text-gray-700 cursor-pointer">
                <input type="checkbox" checked={form.consent_background_check} onChange={(e) => setForm({ ...form, consent_background_check: e.target.checked })} className="mt-1 accent-warm-700" data-testid="vol-consent" />
                <span>{c.labels.consent}</span>
              </label>

              <Button type="submit" disabled={submitting} className="bg-warm-700 hover:bg-warm-800 text-white rounded-full px-8 py-5" data-testid="volunteer-submit">
                {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {c.labels.submitting}</> : c.labels.submit}
              </Button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

function Field({ label, value, onChange, required, type = "text", placeholder, testId }) {
  return (
    <div>
      <label className="text-xs font-medium text-gray-600 uppercase tracking-wider block mb-1">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <input type={type} required={required} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm" data-testid={testId} />
    </div>
  );
}
